import {State, Action, Selector, StateContext, Store} from '@ngxs/store';
import {LoggerService} from '@app/service/logger/logger.service';
import {
  AddPaymentMethod, AddPaymentMethodOneTime, DeleteInvoice, DeleteInvoiceItem, DeletePaymentMethod, GetInvoices, GetPaymentIntent,
  GetPaymentMethods, GetStripeBilling, PayInvoice, SetPaymentMethod
} from '@shared/state/billing/billing.actions';
import {BillingService} from '@shared/service/billing/billing.service';
import {ToastService} from '@app/service/util/toast.service';
import {ServerResponse} from '@shared/interfaces';
import {serverResponseToast} from '@shared/helpers/helpers';
import {AnalyticsService} from '@app/service/util/analytics.service';
import {StorageService} from '@app/service/util/storage.service';
import {UNSAVED_PRICE_KEY} from '@shared/helpers/variables';
import {PaymentMethod, PaymentIntent} from '@stripe/stripe-js';
import {Injectable} from '@angular/core';

export interface BillingStateModel {
  stripeCustomerInfo: any;
  defaultPaymentMethod: string;
  payWithPaymentMethod: string;
  paymentIntent: PaymentIntent | { id: string; };
  paymentMethods: PaymentMethod[];
  invoicesInfo: any[];
  loaded: boolean;
}

@State<BillingStateModel>({
  name: 'billing',
  defaults: {
    stripeCustomerInfo: {},
    defaultPaymentMethod: '',
    payWithPaymentMethod: '',
    paymentMethods: null,
    paymentIntent: null,
    loaded: false,
    invoicesInfo: [],
  }
})
@Injectable()
export class BillingState {
  duration = 10000;
  reportPriceKey = UNSAVED_PRICE_KEY;

  constructor(private logger: LoggerService, private billing: BillingService, private store: Store,
              private toast: ToastService, private analytics: AnalyticsService, private storage: StorageService) {

  }

  @Selector()
  static getBillingInfo(state: BillingStateModel) {
    return state.stripeCustomerInfo;
  }

  @Selector()
  static getDefaultPaymentMethod(state: BillingStateModel) {
    return state.defaultPaymentMethod;
  }

  @Selector()
  static getPayWithPaymentMethod(state: BillingStateModel) {
    return state.payWithPaymentMethod;
  }

  @Selector()
  static getPaymentMethods(state: BillingStateModel) {
    return state.paymentMethods;
  }

  @Selector()
  static getInvoicesInfo(state: BillingStateModel) {
    return state.invoicesInfo;
  }

  @Selector()
  static getBillingLoaded(state: BillingStateModel) {
    return state.loaded;
  }

  @Selector()
  static getPaymentIntent(state: BillingStateModel) {
    return state.paymentIntent;
  }

  @Action(AddPaymentMethod)
  addPaymentMethod({getState, patchState}: StateContext<BillingStateModel>, {payload}: AddPaymentMethod) {
    const stripeCustomerId = payload.stripeCustomerId;
    const paymentMethod = payload.paymentMethod;
    const setAsDefaultCard = payload.setAsDefaultCard;
    return this.billing.addPaymentMethod({stripeCustomerId, paymentMethod, setAsDefaultCard})
      .then((res: any) => {
        let initialPaymentMethods = getState().paymentMethods;
        if (!initialPaymentMethods) {
          initialPaymentMethods = [];
        }
        const finalPaymentMethods = initialPaymentMethods.concat([res.data.paymentMethod]);
        let stripeCustomerInfo = getState().stripeCustomerInfo;
        let defaultPaymentMethod = '';
        if (res.data.stripeCustomerInfo) {
          stripeCustomerInfo = res.data.stripeCustomerInfo;
        }
        if (res.data.defaultPaymentMethod) {
          defaultPaymentMethod = res.data.defaultPaymentMethod;
        }
        patchState({
          paymentMethods: finalPaymentMethods,
          stripeCustomerInfo,
          defaultPaymentMethod,
          payWithPaymentMethod: defaultPaymentMethod
        });
        serverResponseToast(this.toast, res);
        return this.analytics.trackBusinessMetric('payments', 'pm', 'create', 'success');
      }).catch(err => {
        this.toast.error(err.message);
      });
  }

  @Action(AddPaymentMethodOneTime)
  addPaymentMethodOneTime({getState, patchState}: StateContext<BillingStateModel>, {payload}: AddPaymentMethodOneTime) {
    const paymentMethodData = payload.paymentMethodData;

    let initialPaymentMethods = getState().paymentMethods;
    if (!initialPaymentMethods) {
      initialPaymentMethods = [];
    }
    const finalPaymentMethods = initialPaymentMethods.concat([paymentMethodData]);
    patchState({
      paymentMethods: finalPaymentMethods,
      payWithPaymentMethod: paymentMethodData.id
    });
    this.toast.success(`Card ending in ${paymentMethodData.card.last4} will be used to pay for this report.`, 'It will not be saved');
    return this.analytics.trackBusinessMetric('payments', 'pm', 'create', 'success');
  }

  @Action(SetPaymentMethod)
  setPaymentMethod({patchState}: StateContext<BillingStateModel>, {payload}: SetPaymentMethod) {
    const stripeCustomerId = payload.stripeCustomerId;
    const paymentMethod = payload.paymentMethodId;

    return this.billing.setDefaultPaymentMethod({
      paymentMethod, stripeCustomerId
    }).then((res: any) => {
      serverResponseToast(this.toast, res);
      patchState({
        stripeCustomerInfo: res.data.stripeCustomerInfo,
        defaultPaymentMethod: res.data.defaultPaymentMethod,
        payWithPaymentMethod: res.data.defaultPaymentMethod
      });
      return this.analytics.trackBusinessMetric('payments', 'pm', 'edit', 'success');
    }).catch(err => {
      this.toast.error(err.message);
    });
  }

  @Action(DeletePaymentMethod)
  deletePaymentMethod({getState, patchState}: StateContext<BillingStateModel>, {payload}: DeletePaymentMethod) {
    const stripeCustomerId = payload.stripeCustomerId;
    const paymentMethod = payload.paymentMethodId;

    return this.billing.deletePaymentMethod({
      paymentMethod, stripeCustomerId
    }).then((res: any) => {
      serverResponseToast(this.toast, res);
      const paymentMethods = getState().paymentMethods || [];
      let payWithPaymentMethod = getState().payWithPaymentMethod;
      const newPaymentMethods = [];
      for (const method of paymentMethods) {
        if (method.id !== paymentMethod) {
          newPaymentMethods.push(method);
        }
      }
      if (res.data) {
        // If no pay with method or the deleted payment method is the pay with payment method, set to new default if any
        if (!payWithPaymentMethod || payWithPaymentMethod === paymentMethod) {
          payWithPaymentMethod = res.data.defaultPaymentMethod;
        }
        patchState({
          stripeCustomerInfo: res.data.stripeCustomerInfo,
          defaultPaymentMethod: res.data.defaultPaymentMethod,
          payWithPaymentMethod
        });
      }
      patchState({
        paymentMethods: newPaymentMethods
      });
      return this.analytics.trackBusinessMetric('payments', 'pm', 'delete', 'success');
    }).catch(err => {
      this.toast.error(err.message);
    });
  }

  @Action(GetPaymentMethods)
  getPaymentMethods({patchState}: StateContext<BillingStateModel>) {
    return this.billing.getPaymentMethods({})
      .then((res: any) => {
        const stripeCustomerInfo = res.data.stripeCustomerInfo;
        patchState({
          stripeCustomerInfo,
          defaultPaymentMethod: res.data.defaultPaymentMethod,
          payWithPaymentMethod: res.data.defaultPaymentMethod,
          paymentMethods: res.data.paymentMethods,
          loaded: true
        });
      })
      .catch(err => {
        this.logger.error(JSON.stringify(err.stack));
        patchState({
          loaded: true
        });
        this.toast.error(err.message, 'Please refresh the page!');
      });
  }

  @Action(GetStripeBilling)
  getStripeBilling({patchState}: StateContext<BillingStateModel>) {
    return this.billing.getStripeBilling({})
      .then((res: any) => {
        const stripeSession = res.data.stripeSession;
        if (!stripeSession) {
          return;
        }
        window.open(stripeSession.url, '_self');

        return this.analytics.trackOutboundMetric('payments', 'stripe-dashboard', 'click', 'success');
      })
      .catch(err => {
        patchState({
          loaded: true
        });
        this.toast.error(err.message, 'Please refresh the page!');
      });
  }

  @Action(GetPaymentIntent)
  getPaymentIntent({patchState}: StateContext<BillingStateModel>, {payload}: GetPaymentIntent) {
    return this.billing.getPaymentIntent({
      ...payload
    })
      .then((res: any) => {
        const paymentIntent = res.data.paymentIntent;
        const priceInfo = res.data.priceInfo;
        if (!paymentIntent) {
          return;
        }
        this.storage.saveValue(this.reportPriceKey, priceInfo);
        patchState({
          paymentIntent
        });

        return this.analytics.trackBusinessMetric('payments', 'payment-intent', 'get', 'success');
      })
      .catch(error => {
        // If the attempt to get a payment intent errored out, give the user a free report... fail gracefully!
        patchState({
          loaded: true,
          paymentIntent: {
            id: 'free'
          }
        });
        this.toast.error(error.message, 'Please refresh the page!');
        return this.analytics.trackMissionCritical('payments', 'payment-intent', 'get', 'error', {error});
      });
  }

  @Action(GetInvoices)
  getInvoices({patchState}: StateContext<BillingStateModel>) {
    return this.billing.getInvoices()
      .then((res: ServerResponse) => {
        if (!res) {
          return;
        }
        const invoicesInfo: any[] = res.data.stripeInvoices || [];

        patchState({
          invoicesInfo,
          loaded: true
        });
        return this.analytics.trackBusinessMetric('payments', 'invoices', 'get', 'success');
      })
      .catch(error => {
        patchState({
          loaded: true
        });
        this.toast.error(error.message, 'Please refresh the page!');
        return this.analytics.trackMissionCritical('payments', 'invoices', 'get', 'error', {error});
      });
  }

  @Action(PayInvoice)
  payInvoice({getState, patchState}: StateContext<BillingStateModel>, {payload}: PayInvoice) {
    return this.billing.payInvoice({invoice: payload.invoice})
      .then((res: ServerResponse) => {
        serverResponseToast(this.toast, res);
        const invoice = res.data.stripeInvoice;
        const stripeCustomerInfo = res.data.stripeCustomerInfo;

        const state = getState();
        let invoicesInfo = state.invoicesInfo;
        if (invoice) {
          invoicesInfo = invoicesInfo.filter(curInvoice => curInvoice.id !== invoice.id)
            .reverse().concat(invoice).reverse();
        }
        patchState({
          invoicesInfo,
          stripeCustomerInfo
        });
        return this.analytics.trackBusinessMetric('payments', 'invoices', 'paid', 'success');
      })
      .catch(error => {
        patchState({
          loaded: true
        });
        this.toast.error(error.message, error.subtext);
        return this.analytics.trackMissionCritical('payments', 'invoices', 'paid', 'error', {error});
      });
  }

  @Action(DeleteInvoice)
  deleteInvoice({getState, patchState}: StateContext<BillingStateModel>, {payload}: DeleteInvoice) {
    return this.billing.deleteInvoice({invoiceID: payload.invoiceID, action: payload.action})
      .then((res: ServerResponse) => {
        serverResponseToast(this.toast, res);
        const invoice = res.data;

        if (!invoice) {
          return;
        }

        const state = getState();
        let invoicesInfo = state.invoicesInfo;

        invoicesInfo = invoicesInfo.filter(curInvoice => curInvoice.id !== invoice.id);

        patchState({
          invoicesInfo,
        });
        return this.analytics.trackBusinessMetric('payments', 'invoices', 'delete', 'success');
      }).catch(error => {
        this.logger.error(JSON.stringify(error.stack));
        this.toast.error(error.message);
        return this.analytics.trackMissionCritical('payments', 'invoices', 'delete', 'error', {error});
      });
  }

  @Action(DeleteInvoiceItem)
  deleteInvoiceItem({getState, patchState}: StateContext<BillingStateModel>, {payload}: DeleteInvoiceItem) {
    return this.billing.deleteInvoiceItem({
      invoiceID: payload.invoiceID,
      invoiceItemID: payload.invoiceItemID
    })
      .then((res: ServerResponse) => {
        serverResponseToast(this.toast, res);
        const invoice = res.data.stripeInvoice;

        if (!invoice) {
          return;
        }

        const state = getState();
        const invoicesInfo = state.invoicesInfo.filter(curInvoice => curInvoice.id !== invoice.id).concat(invoice);

        patchState({
          invoicesInfo,
        });
      }).catch(err => {
        this.logger.error(JSON.stringify(err.stack));
        this.toast.error(err.message);
      });
  }

  getPendingInDraft(invoices) {
    let pendingReports = {};
    const draftInvoices = invoices.filter(invoice => invoice.status === 'draft' || invoice.status === 'open');

    for (const invoice of draftInvoices) {
      const lineItems: any[] = invoice.lines.data;
      const lineItemsMeta = lineItems.reduce((result, line) => {
        // Use the unique compound key to store the invoiceID and itemID so the metric can be used elsewhere
        // with the values
        result[`${line.metadata}`] = {
          invoiceID: invoice.id,
          invoiceItemID: line.id
        };
        return result;
      }, {});
      pendingReports = Object.assign({}, pendingReports, lineItemsMeta);
    }
    return pendingReports;
  }
}
